import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { fetchFiles } from '../librarySlice';
import { RouteParams } from '../types';
import FilesEmpty from './FilesEmpty';
import FileGridView from './gridView';
import FileListView from './listView';
import { useQuery } from '../../../hooks/urlHooks';
import { useUserTags } from '../../../hooks/useTags';
import { SortBy, SortOrderBy } from '../../../types';
import LiveRecordingsBanner from './LiveRecordingsBanner';

export type viewType = 'grid' | 'list';

export const Files = () => {
  const { library, upload } = useSelector((state: RootState) => state);
  const { activeUploads } = upload;
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const query = useQuery();
  const { getTag } = useUserTags();
  const { searchQuery } = query;
  const sortBy = (getTag('library-sort-by') as SortBy) || 'createdAt';
  const sortOrder = (getTag('library-sort-order') as SortOrderBy) || 'desc';
  const { files, lastRefreshTriggered } = library;
  const { status } = files;

  // Get view from URL or fallback to saved preference
  const viewFromUrl = query.view as viewType | undefined;
  const savedView = getTag('library-view-type') as viewType | null;
  const view = viewFromUrl || savedView || 'grid';

  useEffect(() => {}, [query]);

  // Refetch logic
  const refetchFiles = () => {
    dispatch(
      fetchFiles({
        teamId,
        folderId,
        page: 1,
        limit: 20,
        searchQuery,
        sortBy,
        sortOrder,
      }),
    );
  };

  // Initial fetch + refetch on dependency change
  useEffect(() => {
    refetchFiles();
  }, [teamId, folderId, view, searchQuery, sortBy, sortOrder, dispatch]);

  // Refetch when the refresh timestamp changes (from socket)
  useEffect(() => {
    if (lastRefreshTriggered > 0) {
      // Check if timestamp is valid
      refetchFiles();
    }
  }, [lastRefreshTriggered]); // Add the timestamp as a dependency

  if (!teamId || !folderId) {
    return null;
  }

  if (
    status === 'succeeded' &&
    files.data?.length === 0 &&
    activeUploads.length === 0
  ) {
    return (
      <div className="px-4 pb-4 relative">
        <LiveRecordingsBanner teamId={teamId} folderId={folderId} view={view} />
        <FilesEmpty />
      </div>
    );
  }

  return (
    <div className="px-4 pb-4 relative">
      {view === 'list' ? (
        <FileListView
          liveRecordingsBanner={
            <LiveRecordingsBanner
              teamId={teamId}
              folderId={folderId}
              view="list"
            />
          }
        />
      ) : (
        <FileGridView
          liveRecordingsBanner={
            <LiveRecordingsBanner
              teamId={teamId}
              folderId={folderId}
              view="grid"
            />
          }
        />
      )}
    </div>
  );
};

export default Files;
